import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import { isValid } from 'date-fns';

// Date box with separate dd / mm / yyyy inputs. Replaces MUI's DateField, where a
// click past the text selected the day and Tab the whole date, so editing just
// the month or year meant retyping everything.

const PARTS = [
  { key: 'day', length: 2, placeholder: 'dd', name: 'day' },
  { key: 'month', length: 2, placeholder: 'mm', name: 'month' },
  { key: 'year', length: 4, placeholder: 'yyyy', name: 'year' },
];
const EMPTY_PARTS = { day: '', month: '', year: '' };

const pad2 = (n) => String(n).padStart(2, '0');

// Invisible text that sizes a part's grid cell (see SegmentInputs).
const sizerSx = {
  gridArea: '1 / 1',
  visibility: 'hidden',
  whiteSpace: 'pre',
  px: '1px',
  pointerEvents: 'none',
};

const toParts = (date) => (date instanceof Date && isValid(date)
  ? { day: pad2(date.getDate()), month: pad2(date.getMonth() + 1), year: String(date.getFullYear()) }
  : EMPTY_PARTS);

// A Date only when every part is filled in and names a real day — 31/02 is
// rejected rather than rolled over into March.
const toDate = ({ day, month, year }) => {
  if (!day || !month || year.length !== 4) return null;
  const d = Number(day);
  const m = Number(month);
  const y = Number(year);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d ? date : null;
};

// "15/10/2026", "15-10-2026" or "15.10.2026" — for paste and autofill.
const parseWholeDate = (text) => {
  const match = /^\s*(\d{1,2})\s*[/.-]\s*(\d{1,2})\s*[/.-]\s*(\d{4})\s*$/.exec(text);
  return match ? { day: pad2(match[1]), month: pad2(match[2]), year: match[3] } : null;
};

// The three inputs, as OutlinedInput's inputComponent so they get a normal MUI
// outlined field; InputBase's focus/blur handlers are attached to every part.
const SegmentInputs = forwardRef(function SegmentInputs(props, ref) {
  const { className, onFocus, onBlur, id, segments } = props;
  const { parts, onPartChange, onWholeDate, ariaLabel, pointerXRef } = segments;
  const inputRefs = useRef([]);

  const focusPart = (index) => {
    const input = inputRefs.current[index];
    if (!input) return;
    input.focus();
    input.select();
  };

  const nearestPart = (clientX) => {
    let best = 0;
    let bestDistance = Infinity;
    inputRefs.current.forEach((input, index) => {
      const rect = input.getBoundingClientRect();
      const distance = Math.abs(clientX - (rect.left + rect.width / 2));
      if (distance < bestDistance) {
        best = index;
        bestDistance = distance;
      }
    });
    return best;
  };

  // InputBase calls focus() on this when the field's own padding is clicked;
  // send it to the part nearest the click, or to the first empty part.
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRefs.current.includes(document.activeElement)) return;
      const clickX = pointerXRef.current;
      pointerXRef.current = null;
      if (clickX != null) {
        focusPart(nearestPart(clickX));
        return;
      }
      const firstEmpty = PARTS.findIndex((part) => !parts[part.key]);
      focusPart(firstEmpty === -1 ? 0 : firstEmpty);
    },
    blur: () => inputRefs.current.forEach((input) => input.blur()),
    // InputBase reads `value` to decide whether the field counts as filled.
    get value() {
      return PARTS.map((part) => parts[part.key]).join('');
    },
  }));

  const handleChange = (index) => (event) => {
    const { key, length } = PARTS[index];
    const raw = event.target.value;

    // A whole date arriving at once (autofill, a paste the browser didn't
    // route through onPaste) fills every part.
    const whole = parseWholeDate(raw);
    if (whole) {
      onWholeDate(whole);
      focusPart(PARTS.length - 1);
      return;
    }

    let digits = raw.replace(/\D/g, '').slice(0, length);
    // A first digit that can't start a two-digit day (4–9) or month (2–9) is
    // complete on its own, so move on instead of waiting for a second digit.
    const lonelyDigit = digits.length === 1
      && ((key === 'day' && Number(digits) > 3) || (key === 'month' && Number(digits) > 1));
    if (lonelyDigit) digits = pad2(digits);
    onPartChange(key, digits);
    if (digits.length === length && index < PARTS.length - 1) focusPart(index + 1);
  };

  const handleKeyDown = (index) => (event) => {
    const input = event.currentTarget;
    const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
    const atEnd = input.selectionStart === input.value.length;
    if (event.key === 'Backspace' && input.value === '' && index > 0) {
      event.preventDefault();
      focusPart(index - 1);
    } else if (event.key === 'ArrowLeft' && atStart && index > 0) {
      event.preventDefault();
      focusPart(index - 1);
    } else if (event.key === 'ArrowRight' && atEnd && index < PARTS.length - 1) {
      event.preventDefault();
      focusPart(index + 1);
    } else if (['/', '-', '.'].includes(event.key)) {
      // Typing a separator means "next part", as it would in a plain text box.
      event.preventDefault();
      if (index < PARTS.length - 1) focusPart(index + 1);
    }
  };

  const handlePaste = (event) => {
    const whole = parseWholeDate(event.clipboardData.getData('text'));
    if (!whole) return;
    event.preventDefault();
    onWholeDate(whole);
    focusPart(PARTS.length - 1);
  };

  const handleBlur = (index) => (event) => {
    // "8" becomes "08" once the user leaves the part. Read the input itself:
    // a blur caused by auto-advancing fires before React re-renders, so
    // `parts` here would still hold the previous digit.
    const { key } = PARTS[index];
    const current = event.currentTarget.value;
    if (key !== 'year' && current.length === 1) onPartChange(key, pad2(current));
    onBlur?.(event);
  };

  // Clicks on the separators or the gaps between parts land on the nearest part.
  const handleWrapperMouseDown = (event) => {
    if (inputRefs.current.includes(event.target)) return;
    event.preventDefault();
    focusPart(nearestPart(event.clientX));
  };

  return (
    <Box
      className={className}
      role="group"
      aria-label={ariaLabel}
      onMouseDown={handleWrapperMouseDown}
      // Equal-width digits, so "0000" measures any year the part can hold.
      sx={{ display: 'flex', alignItems: 'center', cursor: 'text', fontVariantNumeric: 'tabular-nums' }}
    >
      {PARTS.map((part, index) => (
        <React.Fragment key={part.key}>
          {index > 0 && (
            <Box component="span" aria-hidden sx={{ px: 0.25, color: 'text.secondary', userSelect: 'none' }}>/</Box>
          )}
          {/* Invisible text in the same grid cell sizes it to the digits (or the
              placeholder while empty), so both read evenly spaced in any font. */}
          <Box sx={{ display: 'inline-grid' }}>
            <Box component="span" aria-hidden sx={sizerSx}>
              {parts[part.key] ? '0'.repeat(part.length) : part.placeholder}
            </Box>
            <Box
              component="input"
              // size=1 keeps the input's own default width (about 20 characters)
              // out of the cell's sizing; width: 100% then fills the cell.
              size={1}
              ref={(element) => { inputRefs.current[index] = element; }}
              id={index === 0 ? id : undefined}
              value={parts[part.key]}
              placeholder={part.placeholder}
              aria-label={`${ariaLabel} ${part.name}`}
              inputMode="numeric"
              autoComplete="off"
              maxLength={part.length}
              onChange={handleChange(index)}
              onKeyDown={handleKeyDown(index)}
              onPaste={handlePaste}
              onFocus={(event) => {
                const input = event.currentTarget;
                // Select the part so typing replaces it. Deferred, because the
                // click that focused the input places the caret afterwards.
                setTimeout(() => { if (document.activeElement === input) input.select(); });
                onFocus?.(event);
              }}
              // A full part (maxLength) would ignore typing at a caret, so a
              // click on an already-focused part selects it too.
              onClick={(event) => event.currentTarget.select()}
              // Safari clears a focus-time selection on mouseup otherwise.
              onMouseUp={(event) => event.preventDefault()}
              onBlur={handleBlur(index)}
              sx={{
                gridArea: '1 / 1',
                width: '100%',
                minWidth: 0,
                p: 0,
                border: 0,
                outline: 0,
                background: 'transparent',
                color: 'inherit',
                font: 'inherit',
                textAlign: 'center',
                '&::placeholder': { color: 'text.disabled', opacity: 1 },
              }}
            />
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
});

// onChange gets a Date, or null while the parts aren't a valid date. `active`
// keeps the field highlighted while the user works in the calendar.
const SegmentedDateField = ({ label, value, onChange, active = false, onActivate, error = false }) => {
  const [parts, setParts] = useState(() => toParts(value));
  // Always the latest parts, even between a change and its re-render, so two
  // updates in one tick (type a digit → auto-advance → blur) can't undo each other.
  const partsRef = useRef(parts);
  const lastEmittedRef = useRef(value);
  const pointerXRef = useRef(null);

  // A date set from outside (a calendar click, reopening the popover)
  // replaces what's shown. Our own emits are skipped, so a half-typed date
  // (emitted as null) doesn't wipe the parts being typed.
  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    partsRef.current = toParts(value);
    setParts(partsRef.current);
  }, [value]);

  const emit = (nextParts) => {
    partsRef.current = nextParts;
    setParts(nextParts);
    const date = toDate(nextParts);
    lastEmittedRef.current = date;
    onChange(date);
  };

  const allFilled = parts.day && parts.month && parts.year.length === 4;
  const invalidDate = Boolean(allFilled) && !toDate(parts);

  return (
    <FormControl variant="outlined" size="small" fullWidth focused={active} error={error || invalidDate}>
      <InputLabel shrink>{label}</InputLabel>
      <OutlinedInput
        label={label}
        notched
        value=""
        onFocus={() => onActivate?.()}
        onMouseDown={(event) => {
          // Remember where the field was clicked; InputBase then calls our
          // focus(), which sends focus to the part nearest this point.
          pointerXRef.current = event.clientX;
          // On the field's own padding, keep focus from dropping to <body>
          // before the click arrives.
          if (event.target === event.currentTarget) event.preventDefault();
        }}
        inputComponent={SegmentInputs}
        inputProps={{
          segments: {
            parts,
            onPartChange: (key, digits) => emit({ ...partsRef.current, [key]: digits }),
            onWholeDate: emit,
            ariaLabel: label,
            pointerXRef,
          },
        }}
      />
    </FormControl>
  );
};

export default SegmentedDateField;
